<template>
  <div
    v-if="(isNpsSurveyFormDisplayed || surveyLink) && isSurveyAvailableForOrder"
    class="cf-card"
  >
    <template v-if="isNpsSurveyFormDisplayed">
      <cf-loading-wrapper
        :is-loading="isSubmittingSurvey"
        :loader-type="'section'"
      >
        <!-- Submit Survey Success CTA -->
        <div
          v-if="isSurveySubmitSuccess"
          class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16 cf-padding-bottom-16 animation-slide-left"
        >
          <span class="cf-icon cf-icon--brand-primary cf-icon--large far fa-check-circle" />

          <h2 class="cf-title cf-title--2 cf-text-align-center">
            {{ surveyContent.surveyCompleteTitle }}
          </h2>

          <p class="cf-subtitle cf-subtitle--2">
            {{ surveyContent.surveyCompleteDescription }}
          </p>
        </div>

        <!-- NPS Survey Question -->
        <div
          v-else
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <cf-radio-input
            v-model="npsSurveyQuestion.answer"
            radio-style="box-small-dynamic"
            :label="npsSurveyQuestion.label"
            :name="`surveyQuestion${ npsSurveyQuestion.id}`"
            :options="npsSurveyQuestion.normalizedOptions"
          />
        </div>
      </cf-loading-wrapper>
    </template>

    <!-- Survey Redirect CTA -->
    <div
      v-if="surveyLink"
      class="cf-flex cf-flex--align-center cf-flex--row cf-flex--justify-space-between cf-flex--gap-16 cf-mobile-column"
    >
      <h3 class="cf-title cf-title--3">
        We'd appreciate your feedback!!!
      </h3>

      <a
        class="cf-button cf-button--primary cf-button--md cf-button--mobile-fill"
        target="_blank"
        :href="surveyLink"
      >
        Take Survey
      </a>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import SurveyService from '@/services/survey';
  import errorHandlers from '@/services/error-handlers';

  import returnExternalHosts from '@/helpers/return-external-hosts';

  import CfRadioInput from '@/view/components/shared/cf-radio-input/index.vue';
  import CfLoadingWrapper from '@/view/components/globals/cf-loading-wrapper/index.vue';


  export default {
    name: 'CfSurveyLink',

    components: {
      CfRadioInput,
      CfLoadingWrapper
    },

    props: {
      order: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        npsSurveyQuestion: { answer: null },
        surveyLink: null,
        isSubmittingSurvey: false,
        isSingleQuestionSurvey: false,
        isNpsSurveyFormDisplayed: false,
        isSurveySubmitSuccess: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),

      isSurveyAvailableForOrder() {
        // Once a survey has been submitted there will be a submittedSurveyId associated with it.
        // Only render the survey if there is no submittedSurveyId.
        return !this.order.submittedSurveyId;
      },

      surveyType() {
        const formTypes = {
          OrderAhead: 'OrderAheadSurvey',
          OrderAndPay: 'OrderAtTableSurvey',
          PayAndFulfill: 'PayAtTableSurvey'
        };

        const formType = formTypes[this.order.orderType] || 'OrderSurvey';

        return formType;
      },

      isReceiptNpsSurveyEnabled() {
        return this.merchant.features['show-nps-survey-on-receipt'];
      },

      isSurveyAutoSubmitEnabled() {
        return this.merchant.options.autoSubmitSurvey;
      },

      surveyContent() {
        const receiptContent = this.content.OrderAheadReceipt;

        return {
          surveyCompleteTitle: receiptContent.metadata.surveyCompleteTitle,
          surveyCompleteDescription: receiptContent.metadata.surveyCompleteDescription
        };
      }
    },

    watch: {
      'npsSurveyQuestion.answer': 'npsSurveyQuestionWatcher'
    },

    created() {
      this.onComponentCreated();
    },

    methods: {
      async onComponentCreated() {
        try {
          const payload = { surveyType: this.surveyType, platform: 'Web' };

          const formData = await SurveyService.getSurvey(payload);

          if (formData) {
            this.setComponentData(formData);
          }
        }
        catch (error) {
          errorHandlers.silent(error);
        }
      },

      setComponentData(formData) {
        const isNpsSurveyFormDisplayed = formData.npsSurveyQuestion && this.isReceiptNpsSurveyEnabled;

        this.surveyId = formData.id;

        this.isSingleQuestionSurvey = formData.isSingleQuestionSurvey;

        this.isNpsSurveyFormDisplayed = isNpsSurveyFormDisplayed;

        if (isNpsSurveyFormDisplayed) {
          this.npsSurveyQuestion = formData.npsSurveyQuestion;
        }
        else {
          this.surveyLink = this.createSurveyRedirectLink();
        }
      },

      createSurveyRedirectLink(prefilledAnswer) {
        const host = returnExternalHosts().selfService;

        let base = `${host}/merchant/${this.merchant.id}/survey/OrderAheadSurvey?orderId=${this.order.orderId}&source=Web`;

        if (prefilledAnswer) {
          base += `&score=${prefilledAnswer}`;
        }

        return base;
      },

      npsSurveyQuestionWatcher(updatedAnswer) {
        /** Note:
         * Can only auto submit NPS survey question if survey has 1 question of type "NetPromoterScore"
         * If survey contains NPS question type and other types cannot auto submit.
         * Once a user has selected NPS answer, create redirect link with prefilled value.
         */
        if (updatedAnswer !== null && this.isSurveyAutoSubmitEnabled && this.isSingleQuestionSurvey) {
          this.submitSurvey();
        }
        else {
          const redirectLink = this.createSurveyRedirectLink(updatedAnswer);

          window.location = redirectLink;
        }
      },

      async submitSurvey() {
        try {
          this.isSubmittingSurvey = true;

          const payload = {
            surveyForm: { questions: [this.npsSurveyQuestion], id: this.surveyId },
            orderId: this.order.orderId
          };

          await SurveyService.submitSurvey(payload);

          this.isSurveySubmitSuccess = true;
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isSubmittingSurvey = false;
        }
      }
    }
  };
</script>
